/**
 * Report Builder component for creating custom reports
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  Schedule as ScheduleIcon,
  DragIndicator as DragIcon,
  Assessment as ReportIcon,
  DateRange as DateIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided } from 'react-beautiful-dnd';

import { analyticsService } from '../../services/analyticsService';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  metrics: string[];
  formats: string[];
  default_format: string;
}

interface DateRangePreset {
  id: string;
  name: string;
}

interface ReportConfig {
  title: string;
  description?: string;
  filters: {
    date_range: {
      start_date: string;
      end_date: string;
    };
    project_codes?: string[];
  };
  metrics: string[];
  format: string;
  include_charts: boolean;
  include_raw_data: boolean;
}

const ReportBuilder: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [dateRangePresets, setDateRangePresets] = useState<DateRangePreset[]>([]);
  const [availableMetrics, setAvailableMetrics] = useState<string[]>([]);
  const [availableFormats, setAvailableFormats] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Report configuration state
  const [reportConfig, setReportConfig] = useState<ReportConfig>({
    title: '',
    description: '',
    filters: {
      date_range: {
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
      }
    },
    metrics: [],
    format: 'pdf',
    include_charts: true,
    include_raw_data: false
  });

  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<string>('last_30_days');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

  const steps = [
    'Configuración Básica',
    'Selección de Métricas',
    'Filtros y Opciones',
    'Revisión y Generación'
  ];

  const metricLabels: Record<string, string> = {
    dashboard: 'Métricas del Dashboard',
    leads: 'Análisis de Leads',
    performance: 'Rendimiento del Sistema',
    conversations: 'Análisis de Conversaciones'
  };

  const formatLabels: Record<string, string> = {
    pdf: 'PDF',
    excel: 'Excel',
    csv: 'CSV',
    json: 'JSON'
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedPreset === 'custom') {
      if (customStartDate && customEndDate) {
        setReportConfig(prev => ({
          ...prev,
          filters: {
            ...prev.filters,
            date_range: {
              start_date: customStartDate.toISOString().split('T')[0],
              end_date: customEndDate.toISOString().split('T')[0]
            }
          }
        }));
      }
    } else {
      const dateRange = getDateRangeFromPreset(selectedPreset);
      setReportConfig(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          date_range: dateRange
        }
      }));
    }
  }, [selectedPreset, customStartDate, customEndDate]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await analyticsService.exportData({
        export_type: 'templates',
        filters: {
          date_range: {
            start_date: new Date().toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0]
          }
        },
        format: 'json',
        include_metadata: false
      });

      // Since the endpoint doesn't exist yet, we'll use mock data
      const mockData = {
        templates: [
          {
            id: 'dashboard_summary',
            name: 'Resumen Dashboard',
            description: 'Métricas principales del dashboard con comparaciones',
            metrics: ['dashboard', 'leads'],
            formats: ['pdf', 'excel', 'csv'],
            default_format: 'pdf'
          },
          {
            id: 'lead_analysis',
            name: 'Análisis de Leads',
            description: 'Análisis detallado de generación y conversión de leads',
            metrics: ['leads', 'conversations'],
            formats: ['pdf', 'excel', 'csv'],
            default_format: 'excel'
          },
          {
            id: 'performance_report',
            name: 'Reporte de Rendimiento',
            description: 'Métricas de rendimiento del sistema y satisfacción',
            metrics: ['performance', 'dashboard'],
            formats: ['pdf', 'excel'],
            default_format: 'pdf'
          }
        ],
        available_metrics: ['dashboard', 'leads', 'performance', 'conversations'],
        available_formats: ['pdf', 'excel', 'csv', 'json'],
        date_range_presets: [
          { id: 'last_7_days', name: 'Últimos 7 días' },
          { id: 'last_30_days', name: 'Últimos 30 días' },
          { id: 'current_month', name: 'Mes actual' },
          { id: 'last_month', name: 'Mes anterior' },
          { id: 'custom', name: 'Rango personalizado' }
        ]
      };

      setTemplates(mockData.templates);
      setAvailableMetrics(mockData.available_metrics);
      setAvailableFormats(mockData.available_formats);
      setDateRangePresets(mockData.date_range_presets);
    } catch (err) {
      setError('Error al cargar las plantillas de reporte');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeFromPreset = (preset: string) => {
    const today = new Date();
    const startDate = new Date();

    switch (preset) {
      case 'last_7_days':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'last_30_days':
        startDate.setDate(today.getDate() - 30);
        break;
      case 'current_month':
        startDate.setDate(1);
        break;
      case 'last_month':
        startDate.setMonth(today.getMonth() - 1, 1);
        today.setDate(0); // Last day of previous month
        break;
      default:
        startDate.setDate(today.getDate() - 30);
    }

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    };
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setReportConfig(prev => ({
        ...prev,
        title: template.name,
        description: template.description,
        metrics: template.metrics,
        format: template.default_format
      }));
    }
  };

  const handleMetricToggle = (metric: string) => {
    setReportConfig(prev => ({
      ...prev,
      metrics: prev.metrics.includes(metric)
        ? prev.metrics.filter(m => m !== metric)
        : [...prev.metrics, metric]
    }));
  };

  const handleMetricReorder = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(reportConfig.metrics);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setReportConfig(prev => ({
      ...prev,
      metrics: items
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const reportData = await analyticsService.exportData({
        export_type: 'report',
        filters: {
          date_range: reportConfig.filters.date_range,
          project_codes: reportConfig.filters.project_codes
        },
        format: reportConfig.format,
        include_metadata: true
      });

      // Create download link
      const blob = new Blob([reportData], {
        type: reportConfig.format === 'pdf' ? 'application/pdf' : 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportConfig.title.replace(/\s+/g, '_')}_${reportConfig.filters.date_range.start_date}.${reportConfig.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError('Error al generar el reporte');
      console.error('Error generating report:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Configuración Básica del Reporte
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Plantilla de Reporte</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  label="Plantilla de Reporte"
                >
                  <MenuItem value="">
                    <em>Reporte personalizado</em>
                  </MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Formato de Salida</InputLabel>
                <Select
                  value={reportConfig.format}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, format: e.target.value }))}
                  label="Formato de Salida"
                >
                  {availableFormats.map((format) => (
                    <MenuItem key={format} value={format}>
                      {formatLabels[format] || format.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título del Reporte"
                value={reportConfig.title}
                onChange={(e) => setReportConfig(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción (opcional)"
                value={reportConfig.description}
                onChange={(e) => setReportConfig(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Selección de Métricas
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Selecciona las métricas que deseas incluir en el reporte. Puedes reordenarlas arrastrándolas.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Métricas Disponibles
                </Typography>
                <List>
                  {availableMetrics.filter(metric => !reportConfig.metrics.includes(metric)).map((metric) => (
                    <ListItem
                      key={metric}
                      button
                      onClick={() => handleMetricToggle(metric)}
                    >
                      <ListItemIcon>
                        <AddIcon />
                      </ListItemIcon>
                      <ListItemText primary={metricLabels[metric] || metric} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Métricas Seleccionadas
                </Typography>
                <DragDropContext onDragEnd={handleMetricReorder}>
                  <Droppable droppableId="selected-metrics">
                    {(provided: DroppableProvided) => (
                      <List {...provided.droppableProps} ref={provided.innerRef}>
                        {reportConfig.metrics.map((metric, index) => (
                          <Draggable key={metric} draggableId={metric} index={index}>
                            {(provided: DraggableProvided) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <ListItemIcon>
                                  <DragIcon />
                                </ListItemIcon>
                                <ListItemText primary={metricLabels[metric] || metric} />
                                <IconButton
                                  edge="end"
                                  onClick={() => handleMetricToggle(metric)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </ListItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </DragDropContext>
              </Paper>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Filtros y Opciones
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Rango de Fechas</InputLabel>
                <Select
                  value={selectedPreset}
                  onChange={(e) => setSelectedPreset(e.target.value)}
                  label="Rango de Fechas"
                >
                  {dateRangePresets.map((preset) => (
                    <MenuItem key={preset.id} value={preset.id}>
                      {preset.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {selectedPreset === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DatePicker
                      label="Fecha Inicio"
                      value={customStartDate}
                      onChange={setCustomStartDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DatePicker
                      label="Fecha Fin"
                      value={customEndDate}
                      onChange={setCustomEndDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Opciones Adicionales
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reportConfig.include_charts}
                    onChange={(e) => setReportConfig(prev => ({ ...prev, include_charts: e.target.checked }))}
                  />
                }
                label="Incluir gráficos y visualizaciones"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reportConfig.include_raw_data}
                    onChange={(e) => setReportConfig(prev => ({ ...prev, include_raw_data: e.target.checked }))}
                  />
                }
                label="Incluir datos sin procesar"
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Revisión del Reporte
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Configuración del Reporte
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Título:
                  </Typography>
                  <Typography variant="body1">
                    {reportConfig.title}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Período:
                  </Typography>
                  <Typography variant="body1">
                    {reportConfig.filters.date_range.start_date} - {reportConfig.filters.date_range.end_date}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Formato:
                  </Typography>
                  <Typography variant="body1">
                    {formatLabels[reportConfig.format]}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Métricas incluidas:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {reportConfig.metrics.map((metric) => (
                      <Chip
                        key={metric}
                        label={metricLabels[metric] || metric}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </Box>

                {reportConfig.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Descripción:
                    </Typography>
                    <Typography variant="body1">
                      {reportConfig.description}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (loading && templates.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Constructor de Reportes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Anterior
            </Button>

            <Box>
              {activeStep === steps.length - 1 && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={() => setPreviewOpen(true)}
                    sx={{ mr: 2 }}
                  >
                    Vista Previa
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                    onClick={() => setScheduleOpen(true)}
                    sx={{ mr: 2 }}
                  >
                    Programar
                  </Button>
                </>
              )}
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleGenerateReport}
                  disabled={loading || !reportConfig.title || reportConfig.metrics.length === 0}
                >
                  {loading ? <CircularProgress size={20} /> : 'Generar Reporte'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    (activeStep === 0 && !reportConfig.title) ||
                    (activeStep === 1 && reportConfig.metrics.length === 0)
                  }
                >
                  Siguiente
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vista Previa del Reporte</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            La vista previa del reporte se mostrará aquí. Esta funcionalidad se implementará en una versión futura.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Programar Reporte</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            La funcionalidad de programación de reportes se implementará en una versión futura.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportBuilder;